import { Box } from "@mui/material";
import { COLORS } from "../assets/theme";
import CustomLabel from "../components/CustomLabel";
import { useLocation, useNavigate } from "react-router-dom";
import CustomButton from "../components/CustomButton";
import GenericFormDialog, { Field } from "../components/GenericFormDialog";
import { getAllBusinesses, createBusiness } from "../services/VenueService";
import AddressWithMapField from "../components/map/AddressWithMapField";

import React, { useState, useEffect } from "react";

const VenueDetails: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const venue = location.state?.venue;

  const [venues, setVenues] = useState<any[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const fields: Field[] = [
    {
      type: "text" as const,
      name: "name",
      label: "Venue Name",
      required: true,
    },
    {
      type: "number" as const,
      name: "orderNumber",
      label: "Order Number",
    },
    { type: "text" as const, name: "sender", label: "Manager" },
    { type: "file" as const, name: "banner", label: "Venue Banner" },
    { type: "text" as const, name: "description", label: "Description" },
    {
      name: "address",
      label: "Address",
      colSpan: 2,
      customRender: (
        value: any,
        onChange: (v: any) => void,
        helpers?: {
          setFieldValue: (name: string, value: any) => void;
          values?: Record<string, any>;
        }
      ) => (
        <AddressWithMapField value={value} onChange={onChange} helpers={helpers} />
      ),
    },
    {
      type: "text" as const,
      name: "priorCancellationHours",
      label: "Prior Cancellation Hours",
    },
    { type: "text" as const, name: "phone", label: "Phone Number" },
    { type: "text" as const, name: "insta_url", label: "Instagram URL" },
  ];

  useEffect(() => {
    const fetchVenues = async () => {
      try {
        setLoading(true);
        const venuesData = await getAllBusinesses();
        setVenues(Array.isArray(venuesData) ? venuesData : []);
      } catch (error) {
        console.error("Failed to fetch venues:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchVenues();
  }, []);

  const handleAddVenue = () => {
    setDialogOpen(true);
  };

  const handleDialogSubmit = async (values: Record<string, any>) => {
    const parsedOrderNumber =
      values.orderNumber !== undefined && values.orderNumber !== ""
        ? parseInt(values.orderNumber as string, 10)
        : undefined;
    try {
      setLoading(true);

      const formData = new FormData();
      formData.append("name", values.name || "Unnamed Venue");
      formData.append("sender", values.sender || "");
      formData.append("description", values.description || "");
      formData.append("address", values.address || "");
      if (values.latitude !== undefined && values.latitude !== "") {
        formData.append("latitude", String(values.latitude));
      }
      if (values.longitude !== undefined && values.longitude !== "") {
        formData.append("longitude", String(values.longitude));
      }
      formData.append("priorCancellationHours", values.priorCancellationHours || "");
      formData.append("phone", values.phone || "");
      formData.append("insta_url", values.insta_url || "");
      if (parsedOrderNumber !== undefined && !Number.isNaN(parsedOrderNumber)) {
        formData.append("orderNumber", String(parsedOrderNumber));
      }
      
      if (values.banner && values.banner instanceof File) {
        formData.append("banner", values.banner);
      }

      const response = await createBusiness(formData);

      if (response && response.banner) {
        const newVenue = {
          name: values.name || "Unnamed Venue",
          banner: response.banner,
          sender: values.sender || "",
          description: values.description || "",
          address: values.address || "",
          latitude: values.latitude || "",
          longitude: values.longitude || "",
          priorCancellationHours: values.priorCancellationHours || "",
          phone: values.phone || "",
          insta_url: values.insta_url || "",
          orderNumber:
            parsedOrderNumber !== undefined && !Number.isNaN(parsedOrderNumber)
              ? parsedOrderNumber
              : undefined,
        };
        setVenues([...venues, newVenue]);
      } else {
        const updatedVenues = await getAllBusinesses();
        setVenues(Array.isArray(updatedVenues) ? updatedVenues : []);
      }
      
      setDialogOpen(false);
    } catch (error) {
      console.error("Failed to create venue:", error);
      const bannerValue = values.banner;
      const bannerPreview =
        bannerValue && typeof bannerValue === "object" && "name" in bannerValue
          ? URL.createObjectURL(bannerValue as File)
          : "/favicon.ico";
      setVenues([
        ...venues,
        {
          name: values.name || "Unnamed Venue",
          banner: bannerPreview,
          sender: values.sender || "",
          description: values.description || "",
          address: values.address || "",
          latitude: values.latitude || "",
          longitude: values.longitude || "",
          priorCancellationHours: values.priorCancellationHours || "",
          phone: values.phone || "",
          insta_url: values.insta_url || "",
          orderNumber:
            parsedOrderNumber !== undefined && !Number.isNaN(parsedOrderNumber)
              ? parsedOrderNumber
              : undefined,
        },
      ]);
      setDialogOpen(false);
    } finally {
      setLoading(false);
    }
  };

  if (!venue) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <CustomLabel
          text="No venue data found."
          fontWeight={700}
          fontSize="1.2rem"
          color={COLORS.MainColor}
        />
        <Box sx={{ mt: 2 }}>
          <button
            onClick={() => navigate(-1)}
            style={{
              padding: "8px 16px",
              borderRadius: 4,
              border: "1px solid",
              cursor: "pointer",
            }}
          >
            Go Back
          </button>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1, sm: 2 }, width: "100%" }}>
      <CustomLabel
        text={`Venue: ${venue?.name || "Venue"}`}
        fontWeight={900}
        fontSize="2rem"
        color={COLORS.MainColor}
        sx={{ mb: 1, mt: 2, textAlign: "center", letterSpacing: 2 }}
      />

      <CustomLabel
        text="My Venues"
        fontWeight={700}
        fontSize="1.2rem"
        color={COLORS.MainColor}
        sx={{ mb: 2, mt: 1, textAlign: "center" }}
      />
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "center", sm: "center" },
          mb: 3,
          gap: { xs: 2, sm: 0 },
        }}
      >
        <CustomLabel
          text={"Choose a venue to continue or"}
          sx={{ display: "inline" }}
        />
        <CustomButton
          variant="outlined"
          label={loading ? "Loading..." : "Add Venue"}
          textColor={COLORS.MainColor}
          borderColor={COLORS.MainColor}
          borderRadius={2}
          sx={{ px: 2, ml: 2, fontWeight: 700 }}
          onClick={handleAddVenue}
          disabled={loading}
        />
        <GenericFormDialog
          open={dialogOpen}
          title="Add New Venue"
          onClose={() => setDialogOpen(false)}
          onSubmit={handleDialogSubmit}
          fields={fields}
        />
      </Box>
      <Box
        sx={{
          display: "grid",
          gap: { xs: 2, sm: 3 },
          p: { xs: 3, sm: 3 },
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, 1fr)",
            md: "repeat(2, 1fr)",
          },
        }}
      >
        {venues.map((venue, idx) => {
          const venueName =
            venue.name && venue.name.trim() ? venue.name : "Unnamed Venue";
          // Display banner URL directly from API response
          const venueBanner =
            venue.banner && venue.banner.trim() ? venue.banner : "/favicon.ico";
          const manager =
            venue.sender && venue.sender.trim() ? venue.sender : "";
          const venueId = encodeURIComponent(venueName);
          return (
            <Box
              key={venueName + idx}
              onClick={() =>
                navigate(`/dashboard/${venueId}`, { state: { venue } })
              }
              sx={{ width: "100%" }}
            >
              <Box
                sx={{
                  bgcolor: COLORS.textFieldBackgroundColor,
                  borderRadius: 6,
                  boxShadow: 2,
                  height: { xs: 100, sm: 80 },
                  display: "flex",
                  alignItems: "center",
                  flexDirection: "row",
                  transition: "box-shadow 0.2s, background 0.2s",
                  cursor: "pointer",
                  p: { xs: 1, sm: 2 },
                  "&:hover": {
                    boxShadow: 6,
                    bgcolor: COLORS.WhiteBackground,
                  },
                }}
              >
                <Box
                  sx={{
                    width: { xs: 50, sm: 60 },
                    height: { xs: 50, sm: 60 },
                    borderRadius: 2,
                    overflow: "hidden",
                    mr: { xs: 1, sm: 2 },
                    flexShrink: 0,
                    background: `url(${venueBanner}) center/cover no-repeat`,
                  }}
                />
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    ml: { xs: 1, sm: 2 },
                    flex: 1,
                  }}
                >
                  <CustomLabel
                    text={venueName + (manager ? ` (${manager})` : "")}
                    fontWeight={700}
                    fontSize="1.2rem"
                    color={COLORS.MainColor}
                    sx={{
                      textAlign: "left",
                      letterSpacing: 1,
                      fontSize: { xs: "1rem", sm: "1.2rem" },
                    }}
                  />
                  {venue.description && (
                    <CustomLabel
                      text={venue.description}
                      fontWeight={400}
                      fontSize="0.95rem"
                      color={COLORS.MainColor}
                      sx={{
                        textAlign: "left",
                        mt: 0.5,
                        fontSize: { xs: "0.85rem", sm: "0.95rem" },
                      }}
                    />
                  )}
                </Box>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default VenueDetails;
